/**
 * Small form controls. Choices are chips (buttons with aria-pressed), never
 * <select>s: on a phone a tap is faster than a native picker, and every extra
 * second on this form costs ad-paid visitors.
 */
import type { Option } from '@codasc/studio-shared';
import type { ReactNode } from 'react';

export function Field({
  label,
  hint,
  error,
  htmlFor,
  optional,
  field,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  htmlFor?: string;
  optional?: boolean;
  /** Error key this field owns, so a failed submit can scroll to it. */
  field?: string;
  children: ReactNode;
}) {
  const Label = htmlFor ? 'label' : 'p';
  return (
    <div className={`st-field${error ? ' has-err' : ''}`} data-field={field}>
      <Label className="st-label" {...(htmlFor ? { htmlFor } : {})}>
        {label}
        {optional ? <span className="st-opt"> · optional</span> : null}
      </Label>
      {hint ? <p className="st-hint">{hint}</p> : null}
      {children}
      {error ? (
        <p className="st-err" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function Choice<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: readonly Option<T>[];
  value: T | null;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="st-chips" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          className="st-chip"
          aria-pressed={value === o.id}
          onClick={() => onChange(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Multi<T extends string>({
  options,
  value,
  onChange,
  label,
  icons,
}: {
  options: readonly Option<T>[];
  value: readonly T[];
  onChange: (v: T[]) => void;
  label: string;
  /** A 24×24 SVG body per option, drawn before its label. */
  icons?: Partial<Record<T, ReactNode>>;
}) {
  const toggle = (id: T) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <div className="st-chips" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          className={`st-chip is-multi${icons?.[o.id] ? ' has-icon' : ''}`}
          aria-pressed={value.includes(o.id)}
          onClick={() => toggle(o.id)}
        >
          {icons?.[o.id] ? (
            <svg className="st-chip-icon" viewBox="0 0 24 24" aria-hidden="true">
              {icons[o.id]}
            </svg>
          ) : null}
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * A required checkbox that is an agreement, not a choice — styled like the
 * consent box, and owning an error key so a failed Continue can scroll to it.
 */
export function Tick({
  checked,
  onChange,
  error,
  field,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  error?: string;
  field: string;
  children: ReactNode;
}) {
  return (
    <div className={`st-consent${error ? ' has-err' : ''}`} data-field={field}>
      <label>
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span>{children}</span>
      </label>
      {error ? (
        <p className="st-err" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Put the first thing that failed on screen and in focus.
 *
 * Inline errors alone are not enough on a phone: the field that needs fixing
 * can be three screens above the button that was just pressed, so the form
 * looks like it did nothing.
 */
export function revealFirstError(errors: Record<string, string>): void {
  const keys = Object.keys(errors);
  if (keys.length === 0) return;
  // The FIRST ONE ON SCREEN, not the first one the checker happened to add:
  // the two orders differ (an RSVP's event type is checked late but sits at
  // the top), and sending someone to the second error reads as a bug.
  const el = [...document.querySelectorAll<HTMLElement>('[data-field]')].find((node) =>
    keys.includes(node.dataset.field ?? ''),
  );
  if (!el) return;
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  const focusable = el.querySelector<HTMLElement>('input, textarea, button, select');
  // Focus after the scroll starts; focusing first would fight it on iOS.
  window.setTimeout(() => focusable?.focus({ preventScroll: true }), 250);
}

/** The Codasc caret mark — the studio wears the company's own brand. */
export function Mark({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <path
        d="M8 25.5 L11 28 L8 30.5"
        stroke="currentColor"
        strokeOpacity="0.85"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="14" y="25.5" width="4" height="5" rx="0.8" fill="currentColor" />
      <path
        className="st-mark-peak"
        d="M12 21 L20 12 L28 21"
        stroke="currentColor"
        strokeWidth="3.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
